import { aiLabelEnabled } from '../../remotion/shared/metadata/ai-label';
import { booleanValue, stringValue } from '../../remotion/shared/metadata/values';

/**
 * Derivation for the header's two clip-wide switches. Both are read back out of
 * the data rather than kept as UI state, so the switch always reports what would
 * actually render.
 *
 * Subtitles are gated by two keys the render path honours: `subtitle_enabled` on
 * a slide's media metadata (the storyboard's per-scene switch, a boolean) and
 * `show_subtitles` on the clip for its own `subtitleText`.
 */

/** A slide shows its text unless `subtitle_enabled` is explicitly off. */
export const slideSubtitleEnabled = (metadata: Record<string, any>): boolean => {
  const raw = metadata?.subtitle_enabled;
  if (raw === undefined || raw === null || raw === '') return true;
  return booleanValue(raw);
};

/** The clip's own subtitle text shows unless `show_subtitles` is explicitly off. */
export const clipSubtitleEnabled = (clipMetadata: Record<string, any>): boolean => {
  const raw = clipMetadata?.show_subtitles;
  if (raw === undefined || raw === null || raw === '') return true;
  return booleanValue(raw);
};

/** Media ids whose metadata carries subtitle text — the only ones a bulk write touches. */
export const subtitleBearingMediaIds = (mediaDrafts: Record<string, Record<string, any>>): string[] =>
  Object.keys(mediaDrafts).filter((mediaId) => stringValue(mediaDrafts[mediaId]?.text).trim() !== '');

export const clipHasSubtitleText = (clipMetadata: Record<string, any>): boolean =>
  stringValue(clipMetadata?.subtitleText).trim() !== '';

/**
 * The switch is on while anything would still be subtitled — so turning it off
 * silences every source, and turning it back on restores all of them.
 */
export const subtitlesEnabled = (
  clipMetadata: Record<string, any>,
  mediaDrafts: Record<string, Record<string, any>>
): boolean => {
  if (clipHasSubtitleText(clipMetadata) && clipSubtitleEnabled(clipMetadata)) return true;
  return subtitleBearingMediaIds(mediaDrafts).some((mediaId) =>
    slideSubtitleEnabled(mediaDrafts[mediaId])
  );
};

/** Is there anything to toggle at all? */
export const hasSubtitleSource = (
  clipMetadata: Record<string, any>,
  mediaDrafts: Record<string, Record<string, any>>
): boolean =>
  clipHasSubtitleText(clipMetadata) || subtitleBearingMediaIds(mediaDrafts).length > 0;

export const nativeAudioEnabled = (clipMetadata: Record<string, any>): boolean =>
  booleanValue(
    clipMetadata?.nativeVideoAudio ?? clipMetadata?.native_video_audio ?? clipMetadata?.nativeAudio
  );

/** The key the renderer reads first, and so the one a write must land on. */
export const NATIVE_AUDIO_CLIP_KEY = 'nativeVideoAudio';

export const AI_LABEL_CLIP_KEY = 'ai_label';

/**
 * The EU AI label is ON unless explicitly off — the renderer's own reader
 * decides, so absence, `true` and junk all read as "shown".
 */
export const aiLabelOn = (clipMetadata: Record<string, any>): boolean =>
  aiLabelEnabled(clipMetadata?.[AI_LABEL_CLIP_KEY] ?? clipMetadata?.aiLabel);
